import { useEffect, useState, useCallback, useMemo } from "react";
import { useRefresh } from "../../contexts/RefreshContext";
import { useThemeMode } from "flowbite-react";
import Chart from "react-apexcharts";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import { getWorkerIdForUser } from "../../services/workers";
import { getAccumulatedSalesData } from "../../services/revenue";
import { getAccumulatedSalesDataForTeam, getCombinedTeamSalesData } from "../../services/teams";
import { useAdminAccess } from "../../hooks/useAdminAccess";
import TeamSelector, { TeamSelection } from "./TeamSelector";

type RevenueData = Record<string, number>;

interface RevenueEntry {
  revenue_date: string;
  revenue: number;
}

interface SalesChartProps {
  teamSelection: TeamSelection;
  onTeamSelectionChange: (selection: TeamSelection) => void;
}

const Y_AXIS_TICK_AMOUNT = 4;
const REVENUE_AXIS_LARGE_STEP_THRESHOLD = 10000;
const CHART_HEIGHT = 350;
const MOBILE_CHART_HEIGHT = 260;

const formatRevenueAxisLabel = (value: number) =>
  `${Math.round(value).toLocaleString("de-DE")}€`;

const getRevenueAxisStep = (maxValue: number) => {
  const stepBase = maxValue >= REVENUE_AXIS_LARGE_STEP_THRESHOLD ? 1000 : 100;
  const rawStep = maxValue / Y_AXIS_TICK_AMOUNT;

  return Math.max(stepBase, Math.ceil(rawStep / stepBase) * stepBase);
};

const getRevenueYAxisRange = (values: number[]) => {
  const maxValue = Math.max(
    0,
    ...values.filter((value) => Number.isFinite(value))
  );
  const step = getRevenueAxisStep(maxValue);

  return {
    min: 0,
    max: step * Y_AXIS_TICK_AMOUNT,
    tickAmount: Y_AXIS_TICK_AMOUNT,
  };
};

const getRevenueYAxisOptions = (
  values: number[],
  labelColor: string,
  fontSize: string,
  offsetX?: number
): ApexCharts.ApexOptions["yaxis"] => ({
  ...getRevenueYAxisRange(values),
  forceNiceScale: false,
  decimalsInFloat: 0,
  labels: {
    style: {
      colors: labelColor,
      fontSize,
      fontWeight: 500,
    },
    formatter: formatRevenueAxisLabel,
    ...(offsetX === undefined ? {} : { offsetX }),
  },
});

const SalesChart = function ({
  teamSelection,
  onTeamSelectionChange,
}: SalesChartProps) {
  const { mode } = useThemeMode();
  const { user } = useAuth();
  const { addRefreshListener } = useRefresh();
  const { hasAccess, hasOwnerAccess, roleId } = useAdminAccess();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [thisWeekData, setThisWeekData] = useState<number[]>([
    0, 0, 0, 0, 0, 0, 0,
  ]);
  const [lastWeekData, setLastWeekData] = useState<number[]>([
    0, 0, 0, 0, 0, 0, 0,
  ]);
  const [currentWorkerId, setCurrentWorkerId] = useState<number | null>(null);

  const isDarkTheme = mode === "dark";
  const borderColor = isDarkTheme ? "#374151" : "#F3F4F6";
  const labelColor = isDarkTheme ? "#93ACAF" : "#6B7280";
  const opacityFrom = isDarkTheme ? 0.2 : 0.4;
  const opacityTo = isDarkTheme ? 0.05 : 0.1;

  const chartSeries = useMemo<ApexAxisChartSeries>(
    () => [
      {
        name: "Letzte Woche",
        data: lastWeekData,
      },
      {
        name: "Diese Woche",
        data: thisWeekData,
      },
    ],
    [lastWeekData, thisWeekData]
  );

  const chartOptions = useMemo<ApexCharts.ApexOptions>(() => {
    const revenueValues = [...lastWeekData, ...thisWeekData];

    return {
      chart: {
        type: "area" as const,
        height: CHART_HEIGHT,
        toolbar: {
          show: false,
        },
        background: "transparent",
        redrawOnParentResize: false,
        redrawOnWindowResize: false,
        parentHeightOffset: 0,
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350,
          },
        },
        zoom: {
          enabled: false,
        },
      },
      colors: ["#9CA3AF", "#52BEC7"],
      dataLabels: {
        enabled: false,
      },
      markers: {
        size: 6,
        offsetX: -2,
      },
      stroke: {
        curve: "smooth",
        width: [3, 3],
        lineCap: "round",
      },
      fill: {
        type: "gradient",
        gradient: {
          opacityFrom,
          opacityTo,
          shade: "dark",
          type: "vertical",
        },
      },
      xaxis: {
        categories: ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"],
        labels: {
          style: {
            colors: labelColor,
            fontSize: "14px",
            fontWeight: 500,
          },
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      yaxis: getRevenueYAxisOptions(revenueValues, labelColor, "14px"),
      legend: {
        labels: {
          colors: labelColor,
        },
        position: "top",
        horizontalAlign: "center",
        fontSize: "14px",
        markers: {
          size: 12,
          offsetX: -2,
        },
        itemMargin: {
          horizontal: 20,
          vertical: 8,
        },
        offsetX: 10,
        floating: false,
        width: undefined,
        height: undefined,
      },
      grid: {
        borderColor,
        strokeDashArray: 4,
        padding: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 10,
        },
      },
      tooltip: {
        theme: isDarkTheme ? "dark" : "light",
        x: {
          show: false,
        },
        y: {
          formatter: (value: number) =>
            `${value.toFixed(2).replace(".", ",")}€`,
        },
        style: {
          fontSize: "14px",
        },
        marker: {
          show: true,
        },
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: {
              height: MOBILE_CHART_HEIGHT,
              toolbar: {
                show: false,
              },
              redrawOnParentResize: false,
              redrawOnWindowResize: false,
              parentHeightOffset: 0,
            },
            legend: {
              position: "bottom",
              horizontalAlign: "center",
              offsetY: 12,
              itemMargin: {
                horizontal: 15,
                vertical: 5,
              },
            },
            yaxis: getRevenueYAxisOptions(
              revenueValues,
              labelColor,
              "12px",
              -5
            ),
            xaxis: {
              labels: {
                style: {
                  fontSize: "12px",
                },
              },
              axisBorder: {
                show: false,
              },
            },
            grid: {
              padding: {
                left: 5,
                right: 5,
                bottom: 10,
              },
            },
            stroke: {
              width: [2, 2],
            },
            markers: {
              size: 4,
            },
          },
        },
      ],
    };
  }, [
    borderColor,
    isDarkTheme,
    labelColor,
    lastWeekData,
    opacityFrom,
    opacityTo,
    thisWeekData,
  ]);

  // Initialize current worker and team context
  useEffect(() => {
    const initializeWorkerContext = async () => {
      if (!user?.id) return;

      try {
        const workerId = await getWorkerIdForUser(user.id);
        setCurrentWorkerId(workerId);
      } catch (error) {
        console.error("Error initializing worker context:", error);
      }
    };

    initializeWorkerContext();
  }, [user?.id, hasAccess]);

  const fetchRevenueData = useCallback(async () => {
    if (!user?.id || !currentWorkerId) return;

    try {
      setLoading(true);

      let revenueData;

      // Handle different selection types based on user role and team context
      if (hasAccess || roleId === 3) {
        // Admin users and Team Leads
        switch (teamSelection.type) {
          case "individual":
            // Get individual worker data
            const today = new Date();
            const thisWeekDates: string[] = [];
            const lastWeekDates: string[] = [];

            // Get Monday of this week
            const monday = new Date(today);
            monday.setDate(monday.getDate() - monday.getDay() + 1);

            // Get dates for this week
            for (let i = 0; i < 7; i++) {
              const date = new Date(monday);
              date.setDate(date.getDate() + i);
              const dateStr = date.toISOString().split("T")[0];
              if (dateStr) thisWeekDates.push(dateStr);
            }

            // Get dates for last week
            for (let i = 0; i < 7; i++) {
              const date = new Date(monday);
              date.setDate(date.getDate() - 7 + i);
              const dateStr = date.toISOString().split("T")[0];
              if (dateStr) lastWeekDates.push(dateStr);
            }

            // Get revenue data for both weeks
            const { data: individualData, error: revenueError } = await supabase
              .from("daily_revenue")
              .select("revenue_date, revenue")
              .eq("worker_id", currentWorkerId)
              .in("revenue_date", [...thisWeekDates, ...lastWeekDates]);

            if (revenueError) throw revenueError;

            // Convert to lookup object
            const revenueLookup: RevenueData = {};
            ((individualData || []) as RevenueEntry[]).forEach((entry) => {
              revenueLookup[entry.revenue_date] = entry.revenue;
            });

            // Fill in the data arrays
            revenueData = {
              thisWeek: thisWeekDates.map((date) => revenueLookup[date] || 0),
              lastWeek: lastWeekDates.map((date) => revenueLookup[date] || 0),
            };
            break;

          case "team":
            // Get team data
            revenueData = await getAccumulatedSalesDataForTeam(
              teamSelection.teamId
            );
            break;

          case "all_my_teams":
            // Get data for all teams the worker belongs to
            if (currentWorkerId) {
              revenueData = await getCombinedTeamSalesData(currentWorkerId);
            } else {
              throw new Error("Worker ID not found");
            }
            break;

          case "all":
            // Get all teams data - STRICTLY RESTRICTED for Role 3
            if (roleId === 3) {
              // Should not happen via UI, but as safety fallback
              throw new Error("Unauthorized access to all-teams data");
            }
            revenueData = await getAccumulatedSalesData();
            break;

          default:
            throw new Error("Invalid team selection type");
        }
      } else {
        // Normal users - ALWAYS show individual data only
        const today = new Date();
        const thisWeekDates: string[] = [];
        const lastWeekDates: string[] = [];

        // Get Monday of this week
        const monday = new Date(today);
        monday.setDate(monday.getDate() - monday.getDay() + 1);

        // Get dates for this week
        for (let i = 0; i < 7; i++) {
          const date = new Date(monday);
          date.setDate(date.getDate() + i);
          const dateStr = date.toISOString().split("T")[0];
          if (dateStr) thisWeekDates.push(dateStr);
        }

        // Get dates for last week
        for (let i = 0; i < 7; i++) {
          const date = new Date(monday);
          date.setDate(date.getDate() - 7 + i);
          const dateStr = date.toISOString().split("T")[0];
          if (dateStr) lastWeekDates.push(dateStr);
        }

        // Get revenue data for both weeks
        const { data: individualData, error: revenueError } = await supabase
          .from("daily_revenue")
          .select("revenue_date, revenue")
          .eq("worker_id", currentWorkerId)
          .in("revenue_date", [...thisWeekDates, ...lastWeekDates]);

        if (revenueError) throw revenueError;

        // Convert to lookup object
        const revenueLookup: RevenueData = {};
        ((individualData || []) as RevenueEntry[]).forEach((entry) => {
          revenueLookup[entry.revenue_date] = entry.revenue;
        });

        // Fill in the data arrays
        revenueData = {
          thisWeek: thisWeekDates.map((date) => revenueLookup[date] || 0),
          lastWeek: lastWeekDates.map((date) => revenueLookup[date] || 0),
        };
      }

      setThisWeekData(revenueData.thisWeek);
      setLastWeekData(revenueData.lastWeek);
      setError(null);
    } catch (err) {
      console.error("Error fetching revenue data:", err);
      setError(
        err instanceof Error ? err.message : "Failed to load revenue data"
      );
    } finally {
      setLoading(false);
    }
  }, [
    user?.id,
    teamSelection.type,
    teamSelection.type === "team" ? teamSelection.teamId : null,
    currentWorkerId,
    hasAccess,
    roleId,
  ]);

  useEffect(() => {
    fetchRevenueData();

    // Add refresh listener
    const unsubscribe = addRefreshListener(() => {
      fetchRevenueData();
    });

    // Cleanup
    return () => {
      unsubscribe();
    };
  }, [fetchRevenueData, addRefreshListener]);

  if (loading) {
    return (
      <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-6 xl:p-8">
        <div className="animate-pulse">
          <div className="mb-4 h-8 w-48 rounded bg-gray-200 dark:bg-gray-700"></div>
          <div className="h-[350px] w-full rounded bg-gray-200 dark:bg-gray-700"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-6 xl:p-8">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  const getChartTitle = () => {
    if (hasAccess || roleId === 3) {
      // Admin users and Team Leads
      switch (teamSelection.type) {
        case "individual":
          return "Dein Umsatz";
        case "team":
          return "Team-Umsatz";
        case "all":
          return "Gesamtumsatz";
        default:
          return "Umsatz";
      }
    } else {
      // Normal users - ALWAYS show "Dein Umsatz"
      return "Dein Umsatz";
    }
  };

  return (
    <div className="rounded-lg bg-white p-2 py-4 shadow-md transition-shadow hover:shadow-lg dark:bg-gray-800 sm:p-6 xl:p-8">
      <div className="mb-4 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white md:text-2xl">
            {getChartTitle()}
          </h3>
        </div>

        {(hasAccess || hasOwnerAccess || roleId === 3) && (
          <TeamSelector
            currentSelection={teamSelection}
            onSelectionChange={onTeamSelectionChange}
            className="sm:ml-auto"
            workerId={currentWorkerId}
          />
        )}
      </div>
      <div className="h-[260px] md:h-[350px]">
        <Chart
          options={chartOptions}
          series={chartSeries}
          type="area"
          height={CHART_HEIGHT}
        />
      </div>
    </div>
  );
};

export default SalesChart;
